import type { Span } from '@sentry/core';

import { ELogLevel } from '../shared/constants';
import { ILogContext, ILogMetadata, ILogOptions } from '../shared/types';

import { ConsoleIntegration } from './integrations/console';
import { SentryIntegration } from './integrations/sentry';
import { trackPerformanceFallback } from './utils/performance';
import { LoggerContextManager } from './LoggerContextManager';
import { LogRedactor } from './LogRedactor';
import { ILogEntry, ILoggerConfig, ILoggerConfigPrepared } from './types';

/**
 * Core Logger implementation - Singleton pattern
 * Contains all business logic, integrations, and state management
 */
export class LoggerCore {
	private static instance: LoggerCore;
	private config: ILoggerConfigPrepared;
	private contextManager: LoggerContextManager;
	private redactor: LogRedactor;
	private sentryIntegration?: SentryIntegration;
	private consoleIntegration?: ConsoleIntegration;

	private constructor(config: ILoggerConfig) {
		const preparedConfig: ILoggerConfigPrepared = {
			...this.getDefaultConfig(),
			...config,
		};

		this.config = preparedConfig;

		this.contextManager = new LoggerContextManager(
			preparedConfig.defaultContext
		);
		this.redactor = new LogRedactor(preparedConfig.redactionRules);

		if (this.config.enableSentry && this.config.sentryInstance) {
			this.sentryIntegration = new SentryIntegration(
				this.config.sentryInstance,
				this.config.sentryMinLevel
			);
		}

		if (this.config.enableConsole) {
			this.consoleIntegration = new ConsoleIntegration({
				enableColors: this.config.enableColors,
				enableTimestamps: this.config.enableTimestamps,
			});
		}
	}

	/**
	 * Initialize logger with configuration
	 * @param config - Logger configuration
	 * @returns LoggerCore instance
	 */
	public static init(config: ILoggerConfig): LoggerCore {
		LoggerCore.instance = new LoggerCore(config);
		return LoggerCore.instance;
	}

	/**
	 * Get singleton logger core instance
	 * @throws Error if logger not initialized
	 * @returns LoggerCore instance
	 */
	public static getInstance(): LoggerCore {
		if (!LoggerCore.instance) {
			throw new Error(
				'Logger not initialized. Call Logger.initialize() first.'
			);
		}
		return LoggerCore.instance;
	}

	/**
	 * Reset singleton instance (for testing only)
	 * @internal
	 */
	public static resetInstance(): void {
		LoggerCore.instance = undefined as any;
	}

	/**
	 * Set global context for all loggers
	 * @param context - Context fields to merge
	 */
	public setGlobalContext(context: Partial<ILogContext>): void {
		this.contextManager.setGlobalContext(context);
	}

	/**
	 * Clear global context fields or all global context
	 * @param keys - Optional array of keys to clear
	 */
	public clearGlobalContext(keys?: string[]): void {
		this.contextManager.clearGlobalContext(keys);
	}

	/**
	 * Get global context
	 * @returns Copy of global context
	 */
	public getGlobalContext(): ILogContext {
		return this.contextManager.getGlobalContext();
	}

	/**
	 * Set context for a specific namespace
	 * @param context - Context fields to merge
	 * @param namespace - Namespace to set context for
	 */
	public setContext(context: Partial<ILogContext>, namespace?: string): void {
		this.contextManager.setContext(context, namespace);
	}

	/**
	 * Clear context for a specific namespace
	 * @param keys - Optional array of keys to clear
	 * @param namespace - Namespace to clear context for
	 */
	public clearContext(keys?: string[], namespace?: string): void {
		this.contextManager.clearContext(keys, namespace);
	}

	/**
	 * Get merged context (global + namespace)
	 * Namespace context overrides global context on conflicts
	 * @param namespace - Optional namespace
	 * @returns Merged context
	 */
	public getMergedContext(namespace?: string): ILogContext {
		return this.contextManager.getMergedContext(namespace);
	}

	/**
	 * Core logging method
	 * @param level - Log level
	 * @param message - Log message
	 * @param metadata - Optional metadata
	 * @param namespace - Optional namespace
	 * @param options - Optional log options
	 */
	public log(
		level: ELogLevel,
		message: string,
		metadata?: ILogMetadata,
		namespace?: string,
		options?: ILogOptions
	): void {
		if (level < this.config.minLevel) {
			return;
		}

		let event: string | undefined;

		/** Extract event from metadata if present */
		if (metadata && 'event' in metadata) {
			event = metadata['event'] as string;
			delete metadata['event'];
		}

		const entry: ILogEntry = {
			level,
			message,
			event,
			metadata: metadata
				? this.config.enableRedaction
					? this.redactor.redact(metadata)
					: metadata
				: undefined,
			context: this.getMergedContext(namespace),
			timestamp: new Date(),
			namespace,
		};

		/** Output to console */
		if (this.consoleIntegration) {
			this.consoleIntegration.log(entry);
		}

		if (this.sentryIntegration) {
			/**
			 * Add breadcrumb if:
			 * - options.asBreadcrumb is explicitly true, OR
			 * - options.asBreadcrumb is not set AND enableAutoBreadcrumbs is true
			 */
			const shouldAddBreadcrumb = Boolean(
				options?.asBreadcrumb ?? this.config.enableAutoBreadcrumbs
			);

			if (shouldAddBreadcrumb) {
				this.sentryIntegration.addBreadcrumb(entry);
			}

			/**
			 * Send to Sentry as event only for ERROR level
			 * Other levels (DEBUG, INFO, WARN) are only breadcrumbs
			 */
			if (level === ELogLevel.ERROR) {
				this.sentryIntegration.captureEvent(entry);
			}
		}
	}

	/**
	 * Performance tracking wrapper
	 * @param name - Operation name
	 * @param operation - Operation type
	 * @param callback - Async callback to execute
	 * @param attributes - Optional attributes
	 * @returns Result of callback
	 */
	public async trackPerformance<T>(
		name: string,
		operation: string,
		callback: (span?: Span) => Promise<T>,
		attributes?: Record<string, any>
	): Promise<T> {
		if (this.sentryIntegration) {
			return this.sentryIntegration.trackPerformance(
				name,
				operation,
				callback,
				attributes
			);
		}

		/** Fallback without Sentry */
		return trackPerformanceFallback(
			name,
			operation,
			callback,
			attributes,
			(data) => {
				this.log(ELogLevel.DEBUG, `Performance: ${data.name}`, {
					operation: data.operation,
					duration: data.duration,
					...data.attributes,
				});
			},
			(data) => {
				this.log(ELogLevel.ERROR, `Performance failed: ${data.name}`, {
					operation: data.operation,
					duration: data.duration,
					...data.attributes,
					error: data.error,
				});
			}
		);
	}

	/**
	 * Add breadcrumb (Sentry integration)
	 * @param message - Breadcrumb message
	 * @param category - Optional category
	 * @param level - Optional level
	 * @param data - Optional data
	 * @param namespace - Optional namespace
	 */
	public breadcrumb(
		message: string,
		category?: string,
		level?: ELogLevel,
		data?: Record<string, any>,
		namespace?: string
	): void {
		if (this.sentryIntegration) {
			const entry: ILogEntry = {
				level: level || ELogLevel.INFO,
				message,
				metadata: data
					? this.config.enableRedaction
						? this.redactor.redact(data)
						: data
					: undefined,
				context: this.getMergedContext(namespace),
				timestamp: new Date(),
				category,
			};

			this.sentryIntegration.addBreadcrumb(entry);
		}
	}

	private getDefaultConfig(): Partial<ILoggerConfig> &
		Pick<ILoggerConfigPrepared, 'sentryMinLevel'> {
		return {
			minLevel: ELogLevel.DEBUG,
			enableConsole: true,
			enableSentry: false,
			sentryMinLevel: ELogLevel.ERROR,
			enableColors: true,
			enableTimestamps: true,
			enableRedaction: false,
			enableAutoBreadcrumbs: false,
		};
	}
}
